import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import update from 'immutability-helper';
import { useDrag, useDrop, DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { shortenStringWithTooltip } from '../helper';
import CustomTooltip from '../Tooltip/CustomTooltip';

const style = {
  padding: '0',
  marginBottom: '1rem',
  backgroundColor: 'white',
  cursor: 'pointer',
};

function DragDrogRearrange({ isShow, isToggle, itemList, saveSequence, dynamic, forTabs, forSystemMaster, fromPim, forSpecTemplate }) {
  const [cards, setCards] = useState([]);
  const { t } = useTranslation('common');
  useEffect(() => {
    if (itemList?.length > 0) {
      setCards([...itemList]);
    }
  }, [itemList]);

  const moveCard = (dragIndex, hoverIndex) => {
    const dragRecord = cards[dragIndex];
    setCards(
      update(cards, {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, dragRecord],
        ],
      }),
    );
  };
  const renderCard = (card, index) => {
    return (
      <List
        key={index}
        index={index}
        id={card._id}
        item={card}
        moveCard={moveCard}
        forTabs={forTabs}
        forSystemMaster={forSystemMaster}
        forSpecTemplate={forSpecTemplate}
      />
    );
  };

  const submitSequence = () => {
    if (dynamic) {
      const updatedSequence = cards.map((item, i) => ({
        _id: item._id,
        [`document_${i}`]: item.label,
        sequence: i + 1,
      }));

      const document = {
        sequence: updatedSequence,
      };

      saveSequence(document);
    } else {
      const updatedSequence = cards.map((item, i) => ({
        _id: item._id,
        sequence: fromPim ? i + 2 : i + 1,
      }));

      const document = {
        sequence: updatedSequence,
      };

      saveSequence(document);
    }
  };

  return (
    <Modal className="rearrange-modal common-custom-modal modal-medium right" show={isShow} onHide={isToggle}>
      <Modal.Header>
        <Modal.Title>{t('REARRANGE_SEQUENCE.MODAL_TITLE')}</Modal.Title>
        <span onClick={() => isToggle()} className="icon-close" />
      </Modal.Header>

      <Modal.Body className="p-0 rearrange-body">
        <div className="drag-list-container">
          <DndProvider backend={HTML5Backend}>
            <div>{cards.map((card, i) => renderCard(card, i))}</div>
          </DndProvider>
        </div>
      </Modal.Body>
      <Modal.Footer className="rearrange-footer">
        <div className="w-100 d-flex align-items-center justify-content-end">
          <CustomTooltip tooltipText={t('common:COMMON_LABEL.SAVE')}>
            <button type="button" className="btn p-0" aria-label="Save" onClick={submitSequence}>
              <i className="icon-Tick-1 f-24" />
            </button>
          </CustomTooltip>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

function List({ id, item, index, moveCard, forTabs, forSystemMaster, forSpecTemplate }) {
  const { t } = useTranslation('common');

  const ref = useRef(null);
  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }
      moveCard(dragIndex, hoverIndex, item, index);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  const tabName = () => {
    return forTabs ? (
      <>
        <strong>{t('DRAG_DROP_ITEM.TAB_NAME')}:&nbsp;</strong> <span>{item?.name && shortenStringWithTooltip(item?.name, 30)}</span>
      </>
    ) : (
      <label className="m-0 mr-3">
        <strong>{t('DRAG_DROP_ITEM.DOCUMENT_NAME')}: </strong> <span>{item?.label ? shortenStringWithTooltip(item?.label, 30) : ''}</span>
      </label>
    );
  };

  return (
    <div ref={ref} style={{ ...style, opacity }} data-handler-id={handlerId}>
      <div className="d-flex align-items-center drag-drop-item">
        <div className="move-cursor">
          <i className="icon-Rearrange f-20" />
        </div>
        <div className="ref-code">
          {forSystemMaster ? (
            <span className="truncated-text" title={`${item?.name ?? item?.keyName}`}>
              {item?.name ?? item?.keyName}
            </span>
          ) : forSpecTemplate ? (
            <>
              <label className="m-0 mr-3">
                <strong>{t('DRAG_DROP_ITEM.POM_CODE')}: </strong> <span>{item?.label ? shortenStringWithTooltip(item?.label, 12) : ''}</span>
              </label>
              {item?.description ? (
                <label className="m-0 text-muted d-block">
                  <span>{shortenStringWithTooltip(item.description, 25)}</span>
                </label>
              ) : null}
            </>
          ) : (
            tabName()
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = ({ BOMConfigDetails }) => {
  const { tabs } = BOMConfigDetails;
  return {
    tabs,
  };
};

const mapStateToDispatch = {};

export default connect(mapStateToProps, mapStateToDispatch)(DragDrogRearrange);
